/* eslint-disable complexity */
/* eslint-disable max-lines */
import { IconNames } from '@blueprintjs/icons';
import { type FetchBaseQueryError, skipToken } from '@reduxjs/toolkit/query';
import { DateFormats, formatDate, formatNameLastFirst } from '@strata/helper-functions';
import { ActionButtonPageTitle } from '@strata/react-buttons';
import {
    EscalatedToDisplayTextMap,
    isRequestArchived,
    RequestActionType,
    RequestType,
    RequestTypeDisplayTextMap,
    UserCell,
} from '@strata/react-chat';
import { ButtonWithDropdownMenu } from '@strata/react-dropdown-menu';
import { FormHeaderAndTabbedContent } from '@strata/react-form-header-and-content';
import { InlineMessage } from '@strata/react-inline-message';
import {
    FormTitleLabelValuePair,
    HeaderLabel,
    HeaderLabelValuePair,
} from '@strata/react-label-value-pairs';
import { Icon } from '@strata/react-status-icons';
import {
    type PayerRequestStatus,
    PayerRequestStatusIndicator,
} from '@strata/react-status-indicators';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { type ComponentProps, useMemo, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router';

import { CancelRequestDialog } from 'components/requests/dialogs/CancelRequestDialog';
import { CloseRequestDialog } from 'components/requests/dialogs/CloseRequestDialog';
import { ReopenRequestDialog } from 'components/requests/dialogs/ReopenRequestDialog';
import { RequestApprovalDialog } from 'components/requests/dialogs/RequestApprovalDialog';
import { ResolveAndCloseRequestDialog } from 'components/requests/dialogs/ResolveAndCloseRequestDialog';
import { ResolveRequestDialog } from 'components/requests/dialogs/ResolveRequestDialog';
import { UpdateAssignmentDialog } from 'components/requests/dialogs/UpdateAssignmentDialog';
import { UpdateInternalEscalationStatusDialog } from 'components/requests/dialogs/UpdateInternalEscalationStatusDialog';
import { UpdateRequestDetailsDialog } from 'components/requests/dialogs/UpdateRequestDetailsDialog';
import { UpdateSummaryDialog } from 'components/requests/dialogs/UpdateSummaryDialog';
import { NotesTab } from 'components/requests/NotesTab';
import { OverviewTab } from 'components/requests/OverviewTab';
import { PayerRequestNumber } from 'components/requests/PayerRequestNumber';
import { FmhAiChat } from 'components/shared/FmhAiChat';
import { Permissions } from 'services/constants';
import { type RequestDetails } from 'services/interfaces';
import { AppRoutes } from 'services/routes';
import { useAppSelector } from 'store';
import { usePayerUserPbmAccessMap, useUserNameMap } from 'store/api/authApi';
import { useGetRequestDetailsQuery } from 'store/api/payerRequestApi';
import { useGetAllProductServicesQuery, useGetSegmentsQuery } from 'store/api/referenceApi';
import { useGetMemberDetailsQuery } from 'store/api/scopeAssessorApi';
import { useGetTreatmentsForPatientQuery } from 'store/api/treatmentApi';

const ActionDialogs = {
    CANCEL_REQUEST: 'CANCEL_REQUEST',
    CLOSE_REQUEST: 'CLOSE_REQUEST',
    REOPEN_REQUEST: 'REOPEN_REQUEST',
    REQUEST_APPROVAL: 'REQUEST_APPROVAL',
    RESOLVE_REQUEST: 'RESOLVE_REQUEST',
    RESOLVE_AND_CLOSE_REQUEST: 'RESOLVE_AND_CLOSE_REQUEST',
    UPDATE_ASSIGNMENT: 'UPDATE_ASSIGNMENT',
    UPDATE_DETAILS: 'UPDATE_DETAILS',
    UPDATE_SUMMARY: 'UPDATE_SUMMARY',
    UPDATE_INTERNAL_ESCALATION_STATUS: 'UPDATE_INTERNAL_ESCALATION_STATUS',
};
type ActionDialog = (typeof ActionDialogs)[keyof typeof ActionDialogs];

const ActionDialogComponentMap = {
    [ActionDialogs.CANCEL_REQUEST]: CancelRequestDialog,
    [ActionDialogs.CLOSE_REQUEST]: CloseRequestDialog,
    [ActionDialogs.REOPEN_REQUEST]: ReopenRequestDialog,
    [ActionDialogs.REQUEST_APPROVAL]: RequestApprovalDialog,
    [ActionDialogs.RESOLVE_REQUEST]: ResolveRequestDialog,
    [ActionDialogs.RESOLVE_AND_CLOSE_REQUEST]: ResolveAndCloseRequestDialog,
    [ActionDialogs.UPDATE_ASSIGNMENT]: UpdateAssignmentDialog,
    [ActionDialogs.UPDATE_DETAILS]: UpdateRequestDetailsDialog,
    [ActionDialogs.UPDATE_SUMMARY]: UpdateSummaryDialog,
    [ActionDialogs.UPDATE_INTERNAL_ESCALATION_STATUS]: UpdateInternalEscalationStatusDialog,
};

const RequestTypeWritePermissionMap = {
    [RequestType.ESCALATION]: Permissions.WRITE_PAYER_REQUEST_ESCALATION,
    [RequestType.CHANGE]: Permissions.WRITE_PAYER_REQUEST_CHANGE,
    [RequestType.OTHER]: Permissions.WRITE_PAYER_REQUEST_OTHER,
};

const InternalEscalationStatuses = new Set<PayerRequestStatus>([
    PayerRequestStatusIndicator.Statuses.IN_PROG,
    PayerRequestStatusIndicator.Statuses.ESCALATED,
]);

const getActionButtonPageTitleLabel = (requestDetails: RequestDetails) =>
    isRequestArchived(requestDetails)
        ? 'Archive'
        : RequestTypeDisplayTextMap[requestDetails.requestType];

export const RequestDetailsPage = () => {
    const { compassAiChat, showPayerRequestSubSubTypes } = useFlags<{
        compassAiChat: boolean;
        showPayerRequestSubSubTypes: boolean;
    }>();
    const { payerId, requestId } = useParams() as { payerId: string; requestId: string };
    const navigate = useNavigate();
    const location = useLocation();
    const { permissions } = useAppSelector((state) => state.auth);

    const { data, isFetching } = useGetRequestDetailsQuery({ payerId, requestId });
    const userNameMap = useUserNameMap(payerId);
    const pbmAccessByUpn = usePayerUserPbmAccessMap(payerId);

    const { data: treatments } = useGetTreatmentsForPatientQuery(
        data?.memberId && data.firstName && data.lastName && data.dateOfBirth
            ? {
                  payerId,
                  segmentId: data.segmentId,
                  memberId: data.memberId,
                  patientFirstName: data.firstName,
                  patientLastName: data.lastName,
                  patientDob: data.dateOfBirth,
              }
            : skipToken,
    );
    const { data: products } = useGetAllProductServicesQuery();
    const treatmentProductOidMap = Object.fromEntries(
        (treatments ?? []).map((treatment) => [
            treatment.treatment_id,
            treatment.product_service_oid,
        ]),
    );

    const requestDetails = useMemo(
        () =>
            data
                ? {
                      ...data,
                      submitterFullName: userNameMap[data.submitterUpn],
                      assignedFullName: data.assignedUpn && userNameMap[data.assignedUpn],
                      payerAssignedFullName:
                          data.payerAssignedUpn && userNameMap[data.payerAssignedUpn],
                      activity: data.activity?.map((action) => {
                          const fmhAction =
                              action.fmhAction ||
                              (action.actorUpn ?? '')
                                  .toLowerCase()
                                  .includes('@freemarkethealth.com');
                          return {
                              ...action,
                              actorFullName: action.actorUpn && userNameMap[action.actorUpn],
                              fmhAction,
                              pbmAction:
                                  !fmhAction &&
                                  !!action.actorUpn &&
                                  !!pbmAccessByUpn[action.actorUpn],
                              actionDetail:
                                  action.actionType === RequestActionType.ASSIGN_CL ||
                                  action.actionType === RequestActionType.ASSIGN_UP
                                      ? action.actionDetail && userNameMap[action.actionDetail]
                                      : action.actionDetail,
                          };
                      }),
                      treatments: data.treatmentIds?.map((treatmentId) => ({
                          treatmentId,
                          productServiceOid: treatmentProductOidMap[treatmentId],
                          productServiceDescription:
                              products?.productServiceMap[treatmentProductOidMap[treatmentId]],
                      })),
                  }
                : undefined,
        [data, userNameMap, pbmAccessByUpn, treatmentProductOidMap, products?.productServiceMap],
    );

    const { data: segments } = useGetSegmentsQuery();

    const canWriteRequestType =
        !!requestDetails?.requestType &&
        permissions.includes(RequestTypeWritePermissionMap[requestDetails.requestType]);

    const showEscalatedTo =
        !!requestDetails?.requestType && requestDetails.requestType === RequestType.ESCALATION;

    const showUpdateInternalEscalationStatus =
        showEscalatedTo && InternalEscalationStatuses.has(requestDetails?.requestStatus);

    const [actionDialogToShow, setActionDialogToShow] = useState<ActionDialog>();
    const ActiveActionDialog = ActionDialogComponentMap[actionDialogToShow ?? ''];
    const closeActionDialog = () => setActionDialogToShow(undefined);

    const notArchivedAndNotResolved =
        !!requestDetails &&
        !isRequestArchived(requestDetails) &&
        requestDetails.requestStatus !== PayerRequestStatusIndicator.Statuses.RESOLVED;

    const primaryMenuItems = useMemo(() => {
        const items: ComponentProps<typeof ButtonWithDropdownMenu>['menuItems'] = [];

        // No actions available without write permissions
        if (isFetching || !canWriteRequestType) {
            return items;
        }

        const orderedConditionsAndActions: {
            condition: boolean;
            actionDialog: ActionDialog;
        }[] = [
            {
                condition: showUpdateInternalEscalationStatus,
                actionDialog: ActionDialogs.UPDATE_INTERNAL_ESCALATION_STATUS,
            },
            {
                condition: isRequestArchived(requestDetails),
                actionDialog: ActionDialogs.REOPEN_REQUEST,
            },
            {
                condition:
                    !isRequestArchived(requestDetails) &&
                    requestDetails.requestStatus === PayerRequestStatusIndicator.Statuses.RESOLVED,
                actionDialog: ActionDialogs.CLOSE_REQUEST,
            },
            {
                condition:
                    notArchivedAndNotResolved &&
                    requestDetails.requestStatus !==
                        PayerRequestStatusIndicator.Statuses.APPROV_REQ,
                actionDialog: ActionDialogs.REQUEST_APPROVAL,
            },
            { condition: notArchivedAndNotResolved, actionDialog: ActionDialogs.RESOLVE_REQUEST },
            {
                condition: notArchivedAndNotResolved,
                actionDialog: ActionDialogs.RESOLVE_AND_CLOSE_REQUEST,
            },
            {
                condition: notArchivedAndNotResolved,
                actionDialog: ActionDialogs.CANCEL_REQUEST,
            },
        ];

        orderedConditionsAndActions.forEach(({ condition, actionDialog }) => {
            if (condition) {
                items.push({
                    onItemClick: () => setActionDialogToShow(actionDialog),
                    text: ActionDialogComponentMap[actionDialog].Title,
                });
            }
        });

        return items;
    }, [
        isFetching,
        canWriteRequestType,
        showUpdateInternalEscalationStatus,
        requestDetails,
        notArchivedAndNotResolved,
    ]);

    const secondaryMenuItems = useMemo(() => {
        const items: ComponentProps<typeof ButtonWithDropdownMenu>['menuItems'] = [];

        // No actions available without write permissions
        if (isFetching || !canWriteRequestType) {
            return items;
        }

        const orderedConditionsAndActions: {
            condition: boolean;
            actionDialog: ActionDialog;
        }[] = [
            {
                condition: notArchivedAndNotResolved,
                actionDialog: ActionDialogs.UPDATE_ASSIGNMENT,
            },
            {
                condition: notArchivedAndNotResolved,
                actionDialog: ActionDialogs.UPDATE_DETAILS,
            },
            {
                condition:
                    notArchivedAndNotResolved &&
                    (
                        [
                            RequestType.CHANGE,
                            RequestType.OTHER,
                        ] as (typeof RequestType)[keyof typeof RequestType][]
                    ).includes(requestDetails?.requestType),
                actionDialog: ActionDialogs.UPDATE_SUMMARY,
            },
        ];

        orderedConditionsAndActions.forEach(({ condition, actionDialog }) => {
            if (condition) {
                items.push({
                    text: ActionDialogComponentMap[actionDialog].Title,
                    onItemClick: () => setActionDialogToShow(actionDialog),
                });
            }
        });

        return items;
    }, [isFetching, canWriteRequestType, notArchivedAndNotResolved, requestDetails?.requestType]);

    const requestLoaded = !isFetching && !!requestDetails;

    const { error: memberError } = useGetMemberDetailsQuery(
        {
            payerId,
            segmentId: requestDetails?.segmentId ?? '',
            memberId: requestDetails?.memberId ?? '',
            firstName: requestDetails?.firstName ?? '',
            lastName: requestDetails?.lastName ?? '',
            dateOfBirth: requestDetails?.dateOfBirth ?? '',
            raiseNotFound: false,
        },
        {
            skip: !requestLoaded || requestDetails?.requestType !== RequestType.ESCALATION,
        },
    );

    return (
        <FormHeaderAndTabbedContent
            rowOneContentHeader={
                requestLoaded ? (
                    <>
                        {ActiveActionDialog && (
                            <ActiveActionDialog
                                payerId={payerId}
                                requestDetails={requestDetails}
                                onClose={closeActionDialog}
                            />
                        )}
                        <ActionButtonPageTitle
                            label={getActionButtonPageTitleLabel(requestDetails)}
                            title={`${
                                RequestTypeDisplayTextMap[requestDetails.requestType]
                            } Details`}
                            onClick={() =>
                                navigate({
                                    pathname: AppRoutes.requests,
                                    hash: isRequestArchived(requestDetails)
                                        ? RequestType.ARCHIVE.toLocaleLowerCase()
                                        : requestDetails.requestType?.toLocaleLowerCase(),
                                })
                            }
                        />
                        <div
                            className="flex__gap--small"
                            style={{ marginLeft: 'auto', alignItems: 'center' }}
                        >
                            {compassAiChat && (
                                <FmhAiChat
                                    payerId={payerId}
                                    requestNumber={requestDetails.requestNumber}
                                />
                            )}
                            {primaryMenuItems.length > 0 && (
                                <ButtonWithDropdownMenu
                                    menuItems={primaryMenuItems}
                                    additionalMenuItems={secondaryMenuItems}
                                    intent="primary"
                                    label="Actions"
                                />
                            )}
                        </div>
                    </>
                ) : undefined
            }
            rowTwoContentHeader={
                requestLoaded ? (
                    <>
                        {requestDetails.requestType === RequestType.ESCALATION && (
                            <FormTitleLabelValuePair
                                label={formatNameLastFirst(
                                    requestDetails.lastName,
                                    requestDetails.firstName,
                                )}
                                value={requestDetails.memberId}
                            />
                        )}
                        {requestDetails.requestType !== RequestType.OTHER && (
                            <HeaderLabelValuePair
                                label="Request Type"
                                value={requestDetails.requestSubType}
                            />
                        )}
                        {showPayerRequestSubSubTypes &&
                            requestDetails.requestType === RequestType.ESCALATION && (
                                <HeaderLabelValuePair
                                    label="Request Subtype"
                                    value={requestDetails.typeLevel2}
                                />
                            )}
                        {requestDetails.requestType !== RequestType.ESCALATION && (
                            <HeaderLabelValuePair
                                label={
                                    <span
                                        className="flex__gap--x-small"
                                        style={{ alignItems: 'center' }}
                                    >
                                        <span>Summary</span>
                                        {!requestDetails.summary && (
                                            <Icon
                                                size={12}
                                                icon={IconNames.WARNING_SIGN}
                                                intent={Icon.Intent.WARNING}
                                            />
                                        )}
                                    </span>
                                }
                                value={requestDetails.summary}
                            />
                        )}
                        <HeaderLabelValuePair
                            label="Created On"
                            value={formatDate(requestDetails.createDate, DateFormats.DATE_TIME)}
                        />
                        <div>
                            <HeaderLabel label="Created By" />
                            <UserCell
                                fullName={requestDetails.submitterFullName}
                                upn={requestDetails.submitterUpn}
                            />
                        </div>
                        <HeaderLabelValuePair
                            label="Request Urgency"
                            value={
                                requestDetails.urgent ? (
                                    <div
                                        className="flex__gap--x-small"
                                        style={{ alignItems: 'center' }}
                                    >
                                        <Icon
                                            icon={IconNames.WARNING_SIGN}
                                            intent={Icon.Intent.WARNING}
                                        />
                                        <span>Urgent</span>
                                    </div>
                                ) : undefined
                            }
                        />
                        <HeaderLabelValuePair
                            label="Request #"
                            showLabelTooltip={false}
                            showValueTooltip={false}
                            value={
                                <PayerRequestNumber
                                    text={requestDetails.requestNumber}
                                    pbmOnly={requestDetails.pbmOnly}
                                />
                            }
                        />
                        <HeaderLabelValuePair
                            label="FMH Assignee"
                            value={requestDetails.assignedFullName}
                        />
                        <HeaderLabelValuePair
                            label="Payer Assignee"
                            value={requestDetails.payerAssignedFullName}
                        />
                        <HeaderLabelValuePair
                            label="Payer Segment"
                            value={
                                segments?.find(
                                    (segment) => segment.segmentId === requestDetails.segmentId,
                                )?.name
                            }
                        />
                        {showEscalatedTo && (
                            <HeaderLabelValuePair
                                label="Escalated To"
                                value={
                                    requestDetails.escalatedTo
                                        ? EscalatedToDisplayTextMap[requestDetails.escalatedTo]
                                        : undefined
                                }
                            />
                        )}
                        <div>
                            <HeaderLabel label="Request Status" />
                            <PayerRequestStatusIndicator
                                status={
                                    requestDetails.requestStatus ===
                                    PayerRequestStatusIndicator.Statuses.RESOLVED
                                        ? PayerRequestStatusIndicator.Statuses.MONITORING
                                        : requestDetails.requestStatus
                                }
                            />
                        </div>
                    </>
                ) : undefined
            }
            rowThreeContentHeader={
                requestLoaded &&
                requestDetails?.requestType === RequestType.ESCALATION &&
                (memberError as FetchBaseQueryError)?.status === 404 && (
                    <InlineMessage
                        intent={InlineMessage.Intent.WARNING}
                        label="This member is unverified."
                    />
                )
            }
            tabs={[
                {
                    title: 'Overview',
                    className: 'tabbed-grid-content',
                    mainContent: (
                        <OverviewTab
                            canWriteRequestType={canWriteRequestType}
                            isLoading={isFetching}
                            requestDetails={requestDetails}
                        />
                    ),
                },
                {
                    title: 'Notes',
                    className: 'tabbed-grid-content',
                    mainContent: (
                        <NotesTab
                            canWriteRequestType={canWriteRequestType}
                            isLoading={isFetching}
                            requestDetails={requestDetails}
                        />
                    ),
                },
            ]}
            activeTab={location.hash.replace(/^#/, '').replace(/-/g, ' ')}
            onTabSelect={(tab) =>
                navigate({
                    ...location,
                    hash: tab.replace(/\s/g, '-').toLocaleLowerCase(),
                })
            }
        />
    );
};
